'use client'

import { useId, useState } from 'react'

import Button from '/react4xp/components/prototype/Button'
import Form from '/react4xp/components/prototype/Form/Form'
import Input from '/react4xp/components/prototype/Form/Input'
import Select from '/react4xp/components/prototype/Form/Select'
import Checkbox from '/react4xp/components/prototype/Form/Checkbox'

import type { ContentDownloadFormData, ContentDownloadFormProps } from './types'

// The consent label is assembled as an HTML string (see Checkbox), so a
// caller-supplied URL has to be escaped before it lands in the href attribute.
function escapeAttr(value: string) {
	return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

export default function ContentDownloadForm({
	headline,
	intro,
	formTitle,
	formIntro,
	privacyLink,
	tooltipLabel,
	motivationOptions = [],
	hideResetButton = false,
	secondaryButton,
	labels = {},
	onSubmit,
}: ContentDownloadFormProps) {
	const formId = useId()
	const nameId = `${formId}-name`
	const emailId = `${formId}-email`
	const companyId = `${formId}-company`
	const jobRoleId = `${formId}-job-role`
	const motivationId = `${formId}-motivation`
	const consentId = `${formId}-consent`

	const {
		nameLabel = 'Name',
		emailLabel = 'Email',
		companyLabel = 'Company',
		jobRoleLabel = 'Job role',
		motivationLabel = 'Motivation',
		consentPrefix = 'I have read and agree to the',
		consentLinkLabel = 'privacy policy',
		nameRequiredMessage = 'Name is required',
		emailRequiredMessage = 'Email is required',
		invalidEmailMessage = 'Invalid email address',
		companyRequiredMessage = 'Company is required',
		jobRoleRequiredMessage = 'Job role is required',
		motivationRequiredMessage = 'Motivation is required',
		consentRequiredMessage = 'You must accept the privacy policy',
		sendButtonLabel = 'Submit',
		successTitle = 'Thank you for your inquiry!',
		successSubtitle,
		resetButtonLabel = 'Send another message',
		genericErrorMessage = 'Something went wrong. Please try again.',
	} = labels

	const [isSent, setIsSent] = useState(false)
	const [submitError, setSubmitError] = useState('')

	const hasMotivation = motivationOptions.length > 0
	const hasSecondaryButton = Boolean(secondaryButton?.label && secondaryButton?.link?.url)

	const linkHtml = privacyLink?.url
		? `<a href="${escapeAttr(privacyLink.url)}"${
				privacyLink.isExternal ? ' target="_blank" rel="noopener noreferrer"' : ''
			}>${consentLinkLabel}</a>`
		: consentLinkLabel
	const consentLabel = `${consentPrefix} ${linkHtml}<span aria-hidden="true" class="form-label-required">*</span>`

	async function handleFormSubmit(data: Record<string, unknown>) {
		setSubmitError('')

		const formData: ContentDownloadFormData = {
			name: (data.name as string) ?? '',
			email: (data.email as string) ?? '',
			company: (data.company as string) ?? '',
			jobRole: (data.jobRole as string) ?? '',
			motivation: hasMotivation ? (data.motivation as string) : undefined,
			consent: data.consent === true || data.consent === 'true',
		}

		try {
			await onSubmit?.(formData)
			setIsSent(true)
		} catch {
			setSubmitError(genericErrorMessage)
		}
	}

	function handleReset() {
		setIsSent(false)
		setSubmitError('')
	}

	return (
		<section className="bg-brand-50 py-12">
			<div className="container">
				<div className="flex flex-col lg:flex-row gap-16">
					<div className="min-w-0 flex-1 flex flex-col gap-4">
						{headline && <h2 className="h1 text-black-950">{headline}</h2>}
						{intro && <p className="body text-black-950">{intro}</p>}
					</div>

					<div className="lg:max-w-175 flex-1 flex flex-col gap-6">
						{isSent ? (
							<div className="flex flex-col gap-6 h-full justify-center">
								<h2 className="h2 text-black-950">{successTitle}</h2>
								{successSubtitle && <p className="body text-black-950">{successSubtitle}</p>}
								<div className="flex flex-wrap gap-4">
									{!hideResetButton && (
										<Button variant="primary" size="sm" type="button" className="self-start" onClick={handleReset}>
											{resetButtonLabel}
										</Button>
									)}
									{hasSecondaryButton && (
										<Button
											variant="secondary"
											size="sm"
											href={secondaryButton!.link.url}
											isExternal={secondaryButton!.link.isExternal}
											className="self-start"
										>
											{secondaryButton!.label}
										</Button>
									)}
								</div>
							</div>
						) : (
							<>
								{formTitle && <h2 className="h2 text-black-950">{formTitle}</h2>}
								{formIntro && <p className="body text-black-950">{formIntro}</p>}
								<Form onSubmit={handleFormSubmit} className="flex flex-col gap-6 lg:gap-3">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<Input
											id={nameId}
											name="name"
											label={nameLabel}
											type="text"
											required
											requiredMessage={nameRequiredMessage}
										/>
										<Input
											id={emailId}
											name="email"
											label={emailLabel}
											type="email"
											required
											requiredMessage={emailRequiredMessage}
											invalidEmailMessage={invalidEmailMessage}
										/>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<Input
											id={companyId}
											name="company"
											label={companyLabel}
											type="text"
											required
											requiredMessage={companyRequiredMessage}
										/>
										<Input
											id={jobRoleId}
											name="jobRole"
											label={jobRoleLabel}
											type="text"
											required
											requiredMessage={jobRoleRequiredMessage}
										/>
									</div>

									{hasMotivation && (
										<Select
											id={motivationId}
											name="motivation"
											label={motivationLabel}
											options={motivationOptions}
											required
											requiredMessage={motivationRequiredMessage}
										/>
									)}

									<Checkbox
										id={consentId}
										name="consent"
										label={consentLabel}
										required
										requiredMessage={consentRequiredMessage}
										tooltipLabel={tooltipLabel}
									/>

									{submitError && (
										<p role="alert" className="text-sm leading-[1.4] text-red">
											{submitError}
										</p>
									)}

									<Button variant="primary" size="sm" type="submit" className="self-start mt-2">
										{sendButtonLabel}
									</Button>
								</Form>
							</>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
